import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { ASSETS } from "../../lib/data";

export const Product = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["12%", "-12%"]);

  return (
    <section id="product" ref={ref} className="relative z-10 py-24 md:py-32 border-t border-white/10" data-testid="product-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <SectionKicker>{t.product.kicker}</SectionKicker>
        </Reveal>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-center">
          {/* Product image with spotlight */}
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10 aspect-[3/4] max-h-[620px] mx-auto w-full">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(0,168,107,0.35),transparent_60%)]" />
              <motion.img
                style={{ y: imgY }}
                src={ASSETS.product}
                alt="Gesung Power Storage Generator"
                className="absolute inset-0 w-full h-[124%] object-contain object-center p-6"
              />
              <div className="absolute bottom-5 left-5 text-[11px] font-mono text-white/40 uppercase tracking-[0.25em]">
                GESUNG · GX-Series
              </div>
            </div>
          </Reveal>

          {/* Copy + specs */}
          <div>
            <Reveal>
              <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-5xl leading-[0.95]">
                {t.product.title}
              </h2>
              <p className="mt-5 text-white/60 max-w-lg leading-relaxed">{t.product.sub}</p>
            </Reveal>

            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-xl overflow-hidden">
              {t.product.specs.map((s, i) => (
                <Reveal key={s.k} delay={i * 0.08}>
                  <div className="bg-charcoal p-5 md:p-6 h-full hover:bg-[#202020] transition-colors duration-400">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">{s.k}</p>
                    <p className="font-display text-2xl md:text-3xl font-bold text-lime2 mt-2">{s.v}</p>
                    <p className="text-xs text-white/50 mt-2 leading-snug">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {t.product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-white/75">
                    <span className="mt-0.5 grid place-items-center w-5 h-5 rounded-full bg-emerald2/20 text-emerald2 shrink-0">
                      <Check size={13} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
